<template>
  <article
    class="ncrm-contract-item-field-editor"
    :class="{
      'ncrm-contract-item-field-editor--preview': previewValues,
      'ncrm-contract-item-field-editor--wide': wide,
    }"
  >
    <div class="ncrm-contract-item-field-editor__summary">
      <div class="ncrm-contract-item-field-editor__headline">
        <v-icon size="16" color="primary">
          {{ fieldTypeIcon }}
        </v-icon>
        <div class="text-body-2 ncrm-text-strong ncrm-contract-item-field-editor__title">
          {{ valueOrDash(field?.label) }}
        </div>
        <div v-if="showFieldMeta" class="ncrm-contract-item-field-editor__meta">
          <v-chip size="x-small" color="info">
            {{ fieldTypeLabel }}
          </v-chip>
          <v-chip
            v-if="field?.is_required"
            size="x-small"
            color="warning"
          >
            {{ $t("ncrm.contracts.itemFields.requiredBadge") }}
          </v-chip>
          <v-chip
            size="x-small"
            :color="field?.is_completed ? 'success' : 'default'"
          >
            {{
              field?.is_completed
                ? $t("ncrm.contracts.itemFields.completedBadge")
                : $t("ncrm.contracts.itemFields.pendingBadge")
            }}
          </v-chip>
        </div>
      </div>
      <div
        v-if="field?.help_text"
        class="text-caption text-medium-emphasis ncrm-contract-item-field-editor__help"
      >
        {{ field.help_text }}
      </div>
    </div>

    <div class="ncrm-contract-item-field-editor__input">
      <template v-if="showsEditableInput">
        <NcrmContractItemFieldValueInput
          v-model="localValue"
          :field-type="field?.field_type"
          :label="inputLabel"
          :placeholder="$t('ncrm.contracts.itemFields.valueLabel')"
          :checkbox-label="$t('ncrm.contracts.itemFields.checkboxLabel')"
          :select-options="selectOptions"
          :menu-props="selectMenuProps"
          density="compact"
          :rows="wide ? 3 : 2"
          @enter="$emit('save')"
        />
      </template>
      <div v-else class="ncrm-contract-item-field-editor__display">
        <a
          v-if="displayValueHref"
          class="ncrm-contract-item-field-editor__display-link"
          :href="displayValueHref"
          :target="displayLinkTarget"
          :rel="displayLinkTarget ? 'noopener noreferrer' : null"
        >
          {{ displayValueText }}
          <v-icon
            v-if="displayLinkIsExternal"
            size="13"
            class="ncrm-contract-item-field-editor__display-link-icon"
          >mdi-open-in-new</v-icon>
        </a>
        <div
          v-else
          class="text-body-2"
          :class="{
            'text-medium-emphasis': !hasDisplayValue,
            'ncrm-contract-item-field-editor__display-multiline': normalizedFieldType === 'long_text',
          }"
        >
          {{ displayValueText }}
        </div>
      </div>
    </div>

    <div class="ncrm-contract-item-field-editor__actions">
      <template v-if="previewValues && !editing">
        <v-btn
          size="x-small"
          variant="text"
          color="primary"
          icon="mdi-pencil-outline"
          class="ncrm-contract-item-field-editor__icon-action"
          :aria-label="$t('app.actions.edit')"
          @click="$emit('edit-value')"
        />
      </template>
      <template v-else>
        <v-btn
          :size="previewValues ? 'x-small' : 'small'"
          :variant="previewValues ? 'text' : 'tonal'"
          color="primary"
          :icon="previewValues ? 'mdi-content-save-outline' : undefined"
          :class="{ 'ncrm-contract-item-field-editor__icon-action': previewValues }"
          :aria-label="previewValues ? $t('app.actions.save') : undefined"
          :disabled="!hasChanges || loading"
          :loading="loading"
          @click="$emit('save')"
        >
          <template v-if="!previewValues">
            <v-icon start size="14">mdi-content-save-outline</v-icon>
            {{ $t("app.actions.save") }}
          </template>
        </v-btn>
        <v-btn
          v-if="showResetAction"
          size="x-small"
          variant="text"
          color="secondary"
          icon="mdi-undo-variant"
          class="ncrm-contract-item-field-editor__icon-action"
          :aria-label="$t('app.actions.cancel')"
          @click="$emit('reset')"
        />
      </template>
    </div>
  </article>
</template>

<script>
import NcrmContractItemFieldValueInput from "./NcrmContractItemFieldValueInput.vue";
import { FIELD_TYPE_META } from "./contractItemFieldsShared.mjs";
import { formatDisplayNumber, formatDisplayDate } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmContractItemFieldEditor",
  components: {
    NcrmContractItemFieldValueInput,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: [String, Number, Boolean, null],
      default: null,
    },
    selectOptions: {
      type: Array,
      default: () => [],
    },
    selectMenuProps: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    manual: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    previewValues: {
      type: Boolean,
      default: false,
    },
    showFieldMeta: {
      type: Boolean,
      default: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "edit-value", "save", "reset"],
  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    normalizedFieldType() {
      return String(this.field?.field_type || "").trim().toLowerCase();
    },
    fieldTypeIcon() {
      return FIELD_TYPE_META[this.normalizedFieldType]?.icon || "mdi-form-textbox";
    },
    fieldTypeLabel() {
      const key = `ncrm.contracts.itemFields.types.${this.normalizedFieldType}`;
      const translated = this.$t(key);
      return translated === key ? this.normalizedFieldType : translated;
    },
    inputLabel() {
      if (this.normalizedFieldType === "checkbox") return "";
      return "";
    },
    showsEditableInput() {
      return this.previewValues ? this.editing : true;
    },
    showResetAction() {
      return this.previewValues ? this.editing : this.hasChanges;
    },
    hasDisplayValue() {
      return this.displayValueText !== "-";
    },
    displayValueText() {
      if (this.normalizedFieldType === "checkbox") {
        if (this.modelValue === true) return this.$t("app.labels.yes");
        if (this.modelValue === false) return this.$t("app.labels.no");
        return "-";
      }
      if (this.normalizedFieldType === "number") {
        const parsed = Number(this.modelValue);
        return Number.isFinite(parsed) ? formatDisplayNumber(parsed) : "-";
      }
      if (this.normalizedFieldType === "date") {
        const normalized = String(this.modelValue || "").trim();
        if (!normalized) return "-";
        const parsed = new Date(normalized.replace(" ", "T"));
        if (Number.isNaN(parsed.getTime())) return normalized;
        return formatDisplayDate(parsed);
      }
      if (this.normalizedFieldType === "select") {
        const selected = (Array.isArray(this.selectOptions) ? this.selectOptions : []).find(
          (option) => String(option?.value || "") === String(this.modelValue ?? "")
        );
        return String(selected?.label || this.modelValue || "").trim() || "-";
      }
      return String(this.modelValue || "").trim() || "-";
    },
    displayValueHref() {
      const rawValue = String(this.modelValue || "").trim();
      if (!rawValue) return "";
      if (this.normalizedFieldType === "url") {
        return /^[a-z][a-z0-9+.-]*:\/\//i.test(rawValue) ? rawValue : `https://${rawValue}`;
      }
      if (this.normalizedFieldType === "email") {
        return `mailto:${rawValue}`;
      }
      if (this.normalizedFieldType === "phone") {
        return `tel:${rawValue}`;
      }
      // Auto-detect URLs in text / long_text fields
      if (
        (this.normalizedFieldType === "text" || this.normalizedFieldType === "long_text") &&
        /^https?:\/\//i.test(rawValue)
      ) {
        return rawValue;
      }
      return "";
    },
    displayLinkTarget() {
      if (this.normalizedFieldType === "url") return "_blank";
      // Auto-detected URLs in text fields also open in new tab
      if (
        (this.normalizedFieldType === "text" || this.normalizedFieldType === "long_text") &&
        this.displayValueHref
      ) {
        return "_blank";
      }
      return "";
    },
    displayLinkIsExternal() {
      return Boolean(this.displayLinkTarget === "_blank" && this.displayValueHref);
    },
  },
  methods: {
    valueOrDash(value) {
      const normalized = String(value ?? "").trim();
      return normalized || "-";
    },
  },
};
</script>

<style scoped>
.ncrm-contract-item-field-editor {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.95fr) auto;
  gap: 8px;
  align-items: center;
  padding: 4px 0;
}

.ncrm-contract-item-field-editor--preview {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
}

.ncrm-contract-item-field-editor--wide {
  align-items: start;
}

.ncrm-contract-item-field-editor__summary {
  min-width: 0;
}

.ncrm-contract-item-field-editor__headline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.ncrm-contract-item-field-editor__title {
  min-width: 0;
  line-height: 1.1;
}

.ncrm-contract-item-field-editor__meta {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.ncrm-contract-item-field-editor__help {
  margin-top: 4px;
  padding-left: 26px;
}

.ncrm-contract-item-field-editor__input {
  min-width: 0;
}

.ncrm-contract-item-field-editor__display {
  min-height: 20px;
  display: flex;
  align-items: center;
  line-height: 1.1;
}

.ncrm-contract-item-field-editor__display-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-decoration-color: rgba(var(--v-theme-primary), 0.3);
  text-underline-offset: 2px;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: text-decoration-color 0.15s ease;
}

.ncrm-contract-item-field-editor__display-link:hover {
  text-decoration-color: rgb(var(--v-theme-primary));
}

.ncrm-contract-item-field-editor__display-link-icon {
  opacity: 0.5;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
}

.ncrm-contract-item-field-editor__display-link:hover .ncrm-contract-item-field-editor__display-link-icon {
  opacity: 0.85;
}

.ncrm-contract-item-field-editor__display-multiline {
  white-space: pre-wrap;
}

.ncrm-contract-item-field-editor__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.ncrm-contract-item-field-editor__icon-action {
  min-width: 24px;
}

.ncrm-contract-item-field-editor__icon-action:deep(.v-btn) {
  min-width: 24px;
}

.ncrm-contract-item-field-editor__icon-action:deep(.v-btn__content) {
  min-width: 16px;
}

.ncrm-contract-item-field-editor__icon-action:deep(.v-icon) {
  font-size: 16px;
}

@media (max-width: 1260px) {
  .ncrm-contract-item-field-editor {
    grid-template-columns: minmax(0, 1.1fr) minmax(220px, 0.9fr) auto;
  }

  .ncrm-contract-item-field-editor--preview {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
  }
}

@media (max-width: 959px) {
  .ncrm-contract-item-field-editor {
    grid-template-columns: 1fr;
    gap: 6px;
    align-items: stretch;
  }

  .ncrm-contract-item-field-editor__help {
    padding-left: 0;
  }

  .ncrm-contract-item-field-editor__actions {
    justify-content: flex-start;
  }
}
</style>
